import type { ActivityContext, ActivityHandle } from '../../core/activity';
import { makeDraggable } from '../../shared/drag-drop';
import levelsData from './levels.json';

interface Product {
  emoji: string;
  name: string;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  rounds: number;
  minPriceCents: number;
  maxPriceCents: number;
  stepCents: number;
  products: Product[];
}

interface BillKind {
  cents: number;
  type: 'coin' | 'note';
}

const LEVELS = levelsData.levels as Level[];

/** Bolso BR: moedas (centavos + R$ 1) + notas (R$ 2 a R$ 200). */
// F2 (P-166): no modo adulto a compra é a do dia a dia, não a da lancheira.
const PRODUTOS_ADULTO: Product[] = [
  { emoji: '💡', name: 'Conta de luz' },
  { emoji: '🚌', name: 'Passagem de ônibus' },
  { emoji: '📱', name: 'Recarga do celular' },
  { emoji: '🍞', name: 'Pão da semana' },
  { emoji: '☕', name: 'Café' },
  { emoji: '🧺', name: 'Cesta básica' },
  { emoji: '💊', name: 'Remédio' },
  { emoji: '⛽', name: 'Combustível' },
];

const BILLS: BillKind[] = [
  { cents: 5, type: 'coin' },
  { cents: 10, type: 'coin' },
  { cents: 25, type: 'coin' },
  { cents: 50, type: 'coin' },
  { cents: 100, type: 'coin' },
  { cents: 200, type: 'note' },
  { cents: 500, type: 'note' },
  { cents: 1000, type: 'note' },
  { cents: 2000, type: 'note' },
  { cents: 5000, type: 'note' },
  { cents: 10000, type: 'note' },
  { cents: 20000, type: 'note' },
];

function fmtBRL(cents: number): string {
  const reais = Math.floor(cents / 100);
  const c = cents % 100;
  return `R$ ${reais},${String(c).padStart(2, '0')}`;
}

function randInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickPriceCents(level: Level): number {
  const steps = Math.floor((level.maxPriceCents - level.minPriceCents) / level.stepCents);
  const k = randInt(0, steps);
  return level.minPriceCents + k * level.stepCents;
}

export function mountMoneyCents(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0]!;

  let roundIndex = 0;
  let product: Product = { emoji: '', name: '' };
  let priceCents = 0;
  let tray: number[] = [];
  let correctCount = 0;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Dinheiro com centavos</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage money">
      <div class="feedback" aria-live="polite"></div>
      <p class="money__progress" aria-live="polite"></p>
      <div class="money__product">
        <div class="money__product-emoji"></div>
        <div class="money__product-info">
          <div class="money__product-name"></div>
          <div class="money__product-price"></div>
        </div>
      </div>
      <div class="money__tray-wrap">
        <div class="money__tray-label">Sua bandeja</div>
        <div class="money__tray" role="list" aria-label="Bandeja de pagamento"></div>
        <div class="money__total">Total: <strong class="money__total-value">R$ 0,00</strong></div>
      </div>
      <div class="money__bills" role="group" aria-label="Moedas e notas disponíveis"></div>
      <button class="btn btn--primary money__ok" type="button">Pagar</button>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.money__progress')!;
  const emojiEl = page.querySelector<HTMLElement>('.money__product-emoji')!;
  const nameEl = page.querySelector<HTMLElement>('.money__product-name')!;
  const priceEl = page.querySelector<HTMLElement>('.money__product-price')!;
  const trayEl = page.querySelector<HTMLElement>('.money__tray')!;
  const totalEl = page.querySelector<HTMLElement>('.money__total-value')!;
  const billsEl = page.querySelector<HTMLElement>('.money__bills')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.money__ok')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function renderBills(): void {
    billsEl.innerHTML = '';
    trayEl.dataset['dropTray'] = '1';
    BILLS.forEach((b) => {
      const el = document.createElement('button');
      el.type = 'button';
      // moedas em estilo "cents" (amarelas), notas com classes do money normal
      const isCents = b.cents < 200;
      const cls = isCents
        ? 'money__bill money__bill--cents'
        : `money__bill money__bill--${b.type} money__bill--v${b.cents / 100}`;
      el.className = cls;
      el.dataset.cents = String(b.cents);
      el.textContent = fmtBRL(b.cents);
      el.setAttribute('aria-label', `Adicionar ${b.type === 'coin' ? 'moeda' : 'nota'} de ${fmtBRL(b.cents)}`);
      el.addEventListener('click', () => addToTray(b.cents));
      billsEl.appendChild(el);
      makeDraggable(el, {
        getDropTarget: (x, y) => {
          const e = document.elementFromPoint(x, y);
          return e?.closest<HTMLElement>('[data-drop-tray]') ?? null;
        },
        onDrop: () => addToTray(b.cents),
      });
    });
  }

  function renderTray(): void {
    trayEl.innerHTML = '';
    if (tray.length === 0) {
      const empty = document.createElement('span');
      empty.className = 'money__tray-empty';
      empty.textContent = 'Bandeja vazia';
      trayEl.appendChild(empty);
    } else {
      tray.forEach((v, i) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'money__tray-chip';
        el.dataset.idx = String(i);
        el.textContent = fmtBRL(v);
        el.setAttribute('aria-label', `Tirar ${fmtBRL(v)} da bandeja`);
        el.addEventListener('click', () => removeFromTray(i));
        trayEl.appendChild(el);
      });
    }
    const total = tray.reduce((a, b) => a + b, 0);
    totalEl.textContent = fmtBRL(total);
    totalEl.classList.toggle('money__total-value--match', total === priceCents);
    totalEl.classList.toggle('money__total-value--over', total > priceCents);
  }

  function addToTray(cents: number): void {
    tray.push(cents);
    ctx.audio.play('click');
    renderTray();
  }

  function removeFromTray(idx: number): void {
    tray.splice(idx, 1);
    ctx.audio.play('click');
    renderTray();
  }

  function startRound(): void {
    const pool = ctx.publico === 'adulto' ? PRODUTOS_ADULTO : level.products;
    product = pool[randInt(0, pool.length - 1)]!;
    priceCents = pickPriceCents(level);
    tray = [];
    renderProgress();
    renderProduct();
    renderTray();
  }

  function renderProgress(): void {
    progressEl.textContent = `Compra ${roundIndex + 1} de ${level.rounds}`;
  }

  function renderProduct(): void {
    emojiEl.textContent = product.emoji;
    nameEl.textContent = product.name;
    priceEl.textContent = fmtBRL(priceCents);
  }

  function showFeedback(kind: 'ok' | 'err', msg = ''): void {
    feedback.textContent = msg || (kind === 'ok' ? '✓' : '✗');
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 700);
  }

  function validate(): void {
    const total = tray.reduce((a, b) => a + b, 0);
    if (total < priceCents) { showFeedback('err', 'Falta'); ctx.audio.play('wrong'); return; }
    if (total > priceCents) { showFeedback('err', 'Passou'); ctx.audio.play('wrong'); return; }
    ctx.audio.play('correct');
    showFeedback('ok');
    correctCount++;
    roundIndex++;
    window.setTimeout(() => {
      if (roundIndex < level.rounds) startRound();
      else finishLevel();
    }, 700);
  }

  okBtn.addEventListener('click', validate);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') { validate(); e.preventDefault(); }
    else if (e.key === 'Backspace' && tray.length > 0) { tray.pop(); renderTray(); e.preventDefault(); }
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você pagou ${correctCount} de ${level.rounds} compras.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) next.addEventListener('click', () => { window.location.hash = `/money_cents/${level.level + 1}`; });
  }

  renderBills();
  startRound();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}
